import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UsersService } from '../../services/users.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import type { CompanyLocation, SalesmanSyncRow, UserGroupOption } from './salesman-app-users.model';

export interface SalesmanCreateAppUserDialogData {
	row: SalesmanSyncRow;
	usergroups: UserGroupOption[];
	locations: CompanyLocation[];
}

@Component({
	selector: 'app-salesman-create-app-user-dialog',
	templateUrl: './salesman-create-app-user-dialog.component.html',
	styleUrls: ['./salesman-create-app-user-dialog.component.scss'],
})
export class SalesmanCreateAppUserDialogComponent implements OnInit {
	saving = false;
	hidePassword = true;

	readonly compareGroupId = (a: unknown, b: unknown) => String(a ?? '') === String(b ?? '');
	readonly compareLoccode = (a: unknown, b: unknown) => String(a ?? '').trim() === String(b ?? '').trim();

	form = new FormGroup({
		userid: new FormControl('', [Validators.required, Validators.pattern(/^[a-zA-Z0-9._-]+$/)]),
		password: new FormControl('', [Validators.required, Validators.minLength(4)]),
		email: new FormControl(''),
		user_group: new FormControl<string | number | ''>('', [Validators.required]),
		defaultlocation: new FormControl('', [Validators.required]),
	});

	constructor(
		private dialogRef: MatDialogRef<SalesmanCreateAppUserDialogComponent, { created: boolean; userid?: string }>,
		@Inject(MAT_DIALOG_DATA) public data: SalesmanCreateAppUserDialogData,
		private usersService: UsersService,
		private globalSearchService: GlobalSearchService
	) {}

	get canSubmit(): boolean {
		return !!(this.data.usergroups?.length && this.data.locations?.length);
	}

	ngOnInit(): void {
		const row = this.data.row;
		const locs = this.data.locations || [];
		const groups = this.data.usergroups || [];

		const defaultLocCode = (row.defaultloc || '').trim();
		const locCodes = new Set(locs.map((l) => String(l.loccode || '').trim()));
		const initialLoc = defaultLocCode && locCodes.has(defaultLocCode) ? defaultLocCode : (locs[0]?.loccode != null ? String(locs[0].loccode) : '');

		const groupId = this.defaultSalesmanGroupId() ?? groups[0]?.user_group_id ?? '';

		this.form.patchValue({
			userid: (row.suggested_userid || '').toUpperCase(),
			password: this.initialPasswordFromExtra(row.smanpass),
			email: row.smanemail || '',
			user_group: groupId,
			defaultlocation: initialLoc,
		});

		if (!locs.length) {
			this.form.get('defaultlocation')?.clearValidators();
			this.form.get('defaultlocation')?.updateValueAndValidity();
		}
	}

	private initialPasswordFromExtra(smanpass: string): string {
		const p = (smanpass || '').trim();
		if (p.length < 4) {
			return '';
		}
		if (/^\$2[aby]\$/.test(p) || /^\$argon2/i.test(p)) {
			return '';
		}
		if (p.length > 200) {
			return '';
		}
		return p;
	}

	cancel(): void {
		this.dialogRef.close({ created: false });
	}

	generatePassword(): void {
		const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
		let pass = '';
		for (let i = 0; i < 8; i++) {
			pass += chars.charAt(Math.floor(Math.random() * chars.length));
		}
		this.form.get('password')?.setValue(pass);
	}

	submit(): void {
		if (!this.canSubmit || this.form.invalid || this.saving) {
			this.form.markAllAsTouched();
			return;
		}
		const row = this.data.row;
		const v = this.form.getRawValue();
		const userid = (v.userid || '').trim().toLowerCase();
		const payload: Record<string, unknown> = {
			userid,
			password: v.password,
			realname: row.salesmanname,
			salesman: row.salesmancode,
			email: (v.email || '').trim(),
			user_group: v.user_group,
			defaultlocation: String(v.defaultlocation || '').trim(),
			fullaccess: 1,
			blocked: row.current === 1 ? 0 : 1,
		};
		this.saving = true;
		this.usersService.createUser(userid, payload).subscribe({
			next: (res: any) => {
				this.saving = false;
				if (res?.success) {
					this.globalSearchService.showNotification('App user created', 'success', 'bottom', 'right');
					this.dialogRef.close({ created: true, userid });
				} else {
					this.globalSearchService.showNotification(res?.message || 'Create failed', 'danger', 'bottom', 'right');
				}
			},
			error: (err) => {
				this.saving = false;
				const msg = err?.error?.message || err?.message || 'Create failed';
				this.globalSearchService.showNotification(msg, 'danger', 'bottom', 'right');
			},
		});
	}

	private defaultSalesmanGroupId(): string | number | null {
		const match = (this.data.usergroups || []).find((g) => (g?.name || '').toLowerCase() === 'salesman');
		if (match?.user_group_id == null || match.user_group_id === '') {
			return null;
		}
		return match.user_group_id;
	}
}
